/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import axios from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { usePermission } from '../../hooks/usePermission';
import { DEFAULT_LANGUAGE, type SupportedLanguage } from '../../utils/language.utils';
import { resolveCoreApiPath } from '../../utils/coreAssetUrl';
import { dataTableStorageTableId, getApplicationCodeForApi, type QubeProDataTableIdentity } from '../../config/app.config';
import { Icon } from './ComponentIcon';
import { TableQuickSearch } from './TableToolsSurface';
import { FormBuilder, type FormField } from './FormBuilder';
import { FormModal } from './FormModal';
import { guidedLearningTargetSelector } from './guided-learning.contract';
import './SmartFilter.css';

export type SmartFilterFieldType = 'text' | 'select' | 'boolean' | 'number' | 'date';
export type SmartFilterOperator =
  | 'contains'
  | 'equals'
  | 'notEquals'
  | 'startsWith'
  | 'endsWith'
  | 'gt'
  | 'lt'
  | 'gte'
  | 'lte'
  | 'between';

export interface SmartFilterOption {
  label: string;
  value: string;
}

export interface SmartFilterField {
  key: string;
  label: string;
  type: SmartFilterFieldType;
  options?: SmartFilterOption[];
}

export interface SmartFilterRule {
  field: string;
  operator: SmartFilterOperator;
  value?: string;
  valueTo?: string;
}

export interface SmartFilterValue {
  version: 1;
  quickSearch?: string;
  logic: 'and' | 'or';
  rules: SmartFilterRule[];
}

interface SmartFilterPreset {
  id: string;
  name: string;
  description?: string;
  definition: SmartFilterValue;
  isDefault?: boolean;
}

interface LookupTarget {
  id: string;
  type: 'user' | 'group' | 'systemGroup';
  code?: string;
  name: string;
  email?: string;
  description?: string;
  memberCount?: number;
}

export interface SmartFilterProps {
  identity: QubeProDataTableIdentity;
  fields: SmartFilterField[];
  value: SmartFilterValue;
  onApply: (value: SmartFilterValue) => void;
  onClear: () => void;
  onAddClick?: () => void;
  addDisabled?: boolean;
  addTitle?: string;
  addButtonClassName?: string;
  /** Guided Learning için kararlı, selector içermeyen ekleme eylemi kimliği. */
  addLearningTargetId?: string;
  hasExternalFilters?: boolean;
  hideQuickSearch?: boolean;
  language?: SupportedLanguage;
}

const EMPTY_FILTER: SmartFilterValue = { version: 1, logic: 'and', rules: [] };

const LABELS = {
  tr: {
    search: 'Ara...',
    savedFilters: 'Kayıtlı filtre',
    allRecords: 'Tüm kayıtlar',
    detail: 'Detaylı filtre',
    clear: 'Filtreleri temizle',
    add: 'Yeni kayıt oluştur',
    modalTitle: 'Akıllı filtre',
    field: 'Alan',
    operator: 'Koşul',
    value: 'Değer',
    valueTo: 'Bitiş',
    addRule: 'Filtre ekle',
    removeRule: 'Filtreyi kaldır',
    apply: 'Uygula',
    save: 'Kaydet',
    saveAs: 'Filtre adı',
    default: 'Öndeğer yap',
    delete: 'Sil',
    onlyMe: 'Yalnız ben',
    target: 'Kullanıcı / grup seç',
    targetSearch: 'Hedef ara...',
    targetRequired: 'Paylaşım hedefi seçin.',
    targetSystemGroup: 'Sistem',
    targetGroup: 'Grup',
    targetUser: 'Kullanıcı',
    nameRequired: 'Filtre adı zorunludur.',
    loading: 'Yükleniyor...',
    noTarget: 'Hedef bulunamadı',
    saved: 'Filtre kaydedildi',
    cancel: 'Vazgeç',
    operators: {
      contains: 'İçerir',
      equals: 'Eşittir',
      notEquals: 'Eşit değildir',
      startsWith: 'İle başlar',
      gte: 'Büyük/eşit',
      lte: 'Küçük/eşit',
      between: 'Arasında',
    },
  },
  en: {
    search: 'Search...',
    savedFilters: 'Saved filter',
    allRecords: 'All records',
    detail: 'Advanced filter',
    clear: 'Clear filters',
    add: 'Create new record',
    modalTitle: 'Smart filter',
    field: 'Field',
    operator: 'Operator',
    value: 'Value',
    valueTo: 'To',
    addRule: 'Add filter',
    removeRule: 'Remove filter',
    apply: 'Apply',
    save: 'Save',
    saveAs: 'Filter name',
    default: 'Make default',
    delete: 'Delete',
    onlyMe: 'Only me',
    target: 'Select user / group',
    targetSearch: 'Search target...',
    targetRequired: 'Select a sharing target.',
    targetSystemGroup: 'System',
    targetGroup: 'Group',
    targetUser: 'User',
    nameRequired: 'Filter name is required.',
    loading: 'Loading...',
    noTarget: 'No target found',
    saved: 'Filter saved',
    cancel: 'Cancel',
    operators: {
      contains: 'Contains',
      equals: 'Equals',
      notEquals: 'Not equals',
      startsWith: 'Starts with',
      gte: 'Greater/equal',
      lte: 'Less/equal',
      between: 'Between',
    },
  },
  ar: {
    search: 'بحث...',
    savedFilters: 'عامل تصفية محفوظ',
    allRecords: 'كل السجلات',
    detail: 'تصفية متقدمة',
    clear: 'مسح عوامل التصفية',
    add: 'إنشاء سجل جديد',
    modalTitle: 'عامل تصفية ذكي',
    field: 'الحقل',
    operator: 'الشرط',
    value: 'القيمة',
    valueTo: 'إلى',
    addRule: 'إضافة عامل تصفية',
    removeRule: 'إزالة عامل التصفية',
    apply: 'تطبيق',
    save: 'حفظ',
    saveAs: 'اسم عامل التصفية',
    default: 'تعيين كافتراضي',
    delete: 'حذف',
    onlyMe: 'أنا فقط',
    target: 'اختيار مستخدم / مجموعة',
    targetSearch: 'البحث عن هدف...',
    targetRequired: 'اختر هدف المشاركة.',
    targetSystemGroup: 'النظام',
    targetGroup: 'مجموعة',
    targetUser: 'مستخدم',
    nameRequired: 'اسم عامل التصفية مطلوب.',
    loading: 'جارٍ التحميل...',
    noTarget: 'لم يتم العثور على هدف',
    saved: 'تم حفظ عامل التصفية',
    cancel: 'إلغاء',
    operators: {
      contains: 'يحتوي على',
      equals: 'يساوي',
      notEquals: 'لا يساوي',
      startsWith: 'يبدأ بـ',
      gte: 'أكبر من أو يساوي',
      lte: 'أصغر من أو يساوي',
      between: 'بين',
    },
  },
};

const OPERATOR_LABELS: Record<SupportedLanguage, Record<SmartFilterOperator, string>> = {
  tr: {
    startsWith: '\u0130le ba\u015flar',
    contains: '\u0130\u00e7erir',
    endsWith: '\u0130le biter',
    equals: 'E\u015fittir',
    notEquals: 'E\u015fit de\u011fildir',
    between: 'Aras\u0131nda',
    gt: 'B\u00fcy\u00fck',
    lt: 'K\u00fc\u00e7\u00fck',
    gte: 'B\u00fcy\u00fck/e\u015fit',
    lte: 'K\u00fc\u00e7\u00fck/e\u015fit',
  },
  en: {
    startsWith: 'Starts with',
    contains: 'Contains',
    endsWith: 'Ends with',
    equals: 'Equals',
    notEquals: 'Not equals',
    between: 'Between',
    gt: 'Greater than',
    lt: 'Less than',
    gte: 'Greater/equal',
    lte: 'Less/equal',
  },
  ar: {
    startsWith: 'يبدأ بـ',
    contains: 'يحتوي على',
    endsWith: 'ينتهي بـ',
    equals: 'يساوي',
    notEquals: 'لا يساوي',
    between: 'بين',
    gt: 'أكبر من',
    lt: 'أصغر من',
    gte: 'أكبر من أو يساوي',
    lte: 'أصغر من أو يساوي',
  },
  de: {
    startsWith: 'Starts with',
    contains: 'Contains',
    endsWith: 'Ends with',
    equals: 'Equals',
    notEquals: 'Not equals',
    between: 'Between',
    gt: 'Greater than',
    lt: 'Less than',
    gte: 'Greater/equal',
    lte: 'Less/equal',
  },
  es: {
    startsWith: 'Starts with',
    contains: 'Contains',
    endsWith: 'Ends with',
    equals: 'Equals',
    notEquals: 'Not equals',
    between: 'Between',
    gt: 'Greater than',
    lt: 'Less than',
    gte: 'Greater/equal',
    lte: 'Less/equal',
  },
  fr: {
    startsWith: 'Starts with',
    contains: 'Contains',
    endsWith: 'Ends with',
    equals: 'Equals',
    notEquals: 'Not equals',
    between: 'Between',
    gt: 'Greater than',
    lt: 'Less than',
    gte: 'Greater/equal',
    lte: 'Less/equal',
  },
  it: {
    startsWith: 'Starts with',
    contains: 'Contains',
    endsWith: 'Ends with',
    equals: 'Equals',
    notEquals: 'Not equals',
    between: 'Between',
    gt: 'Greater than',
    lt: 'Less than',
    gte: 'Greater/equal',
    lte: 'Less/equal',
  },
  ru: {
    startsWith: 'Starts with',
    contains: 'Contains',
    endsWith: 'Ends with',
    equals: 'Equals',
    notEquals: 'Not equals',
    between: 'Between',
    gt: 'Greater than',
    lt: 'Less than',
    gte: 'Greater/equal',
    lte: 'Less/equal',
  },
  zh: {
    startsWith: 'Starts with',
    contains: 'Contains',
    endsWith: 'Ends with',
    equals: 'Equals',
    notEquals: 'Not equals',
    between: 'Between',
    gt: 'Greater than',
    lt: 'Less than',
    gte: 'Greater/equal',
    lte: 'Less/equal',
  },
  ja: {
    startsWith: 'Starts with',
    contains: 'Contains',
    endsWith: 'Ends with',
    equals: 'Equals',
    notEquals: 'Not equals',
    between: 'Between',
    gt: 'Greater than',
    lt: 'Less than',
    gte: 'Greater/equal',
    lte: 'Less/equal',
  },
};

function operatorLabel(language: SupportedLanguage, operator: SmartFilterOperator): string {
  return OPERATOR_LABELS[language]?.[operator] || OPERATOR_LABELS.en[operator] || operator;
}

function getLabels(language: SupportedLanguage) {
  if (language === 'tr') return LABELS.tr;
  if (language === 'ar') return LABELS.ar;
  return LABELS.en;
}

function defaultOperator(field?: SmartFilterField): SmartFilterOperator {
  if (!field) return 'contains';
  if (field.type === 'text') return 'contains';
  if (field.type === 'number' || field.type === 'date') return 'equals';
  return 'equals';
}

function operatorsFor(field?: SmartFilterField): SmartFilterOperator[] {
  if (!field) return ['contains', 'equals'];
  if (field.type === 'text') return ['startsWith', 'contains', 'endsWith'];
  if (field.type === 'number' || field.type === 'date') return ['between', 'equals', 'gt', 'lt'];
  return ['equals', 'notEquals'];
}

function buildRuleFormFields(
  fields: SmartFilterField[],
  rule: SmartFilterRule,
  language: SupportedLanguage,
  labels: ReturnType<typeof getLabels>,
): FormField[] {
  const field = fields.find((item) => item.key === rule.field) || fields[0];
  const operators = operatorsFor(field);
  const effectiveOperators = operators.includes(rule.operator)
    ? operators
    : [rule.operator, ...operators];
  const hasRange = rule.operator === 'between';
  const commonSpan = hasRange ? 3 : 4;
  const valueType: FormField['type'] =
    field?.type === 'select' || field?.type === 'boolean'
      ? 'select'
      : field?.type === 'date'
        ? 'date'
        : field?.type === 'number'
          ? 'number'
          : 'text';

  const ruleFields: FormField[] = [
    {
      name: 'field',
      label: labels.field,
      type: 'select',
      colSpan: commonSpan,
      options: fields.map((item) => ({ value: item.key, label: item.label })),
    },
    {
      name: 'operator',
      label: labels.operator,
      type: 'select',
      colSpan: commonSpan,
      options: effectiveOperators.map((operator) => ({
        value: operator,
        label: operatorLabel(language, operator),
      })),
    },
    {
      name: 'value',
      label: labels.value,
      type: valueType,
      colSpan: commonSpan,
      step: valueType === 'number' ? 'any' : undefined,
      options: valueType === 'select'
        ? (field?.options ?? []).map((option) => ({
            value: option.value,
            label: option.label,
          }))
        : undefined,
    },
  ];

  if (hasRange) {
    ruleFields.push({
      name: 'valueTo',
      label: labels.valueTo,
      type: valueType === 'number' || valueType === 'date' ? valueType : 'text',
      colSpan: 3,
      step: valueType === 'number' ? 'any' : undefined,
    });
  }

  return ruleFields;
}

function hasFilterValue(value: SmartFilterValue): boolean {
  return Boolean(value.quickSearch?.trim() || value.rules.some((rule) => String(rule.value ?? '').trim()));
}

function normalizeFilterValue(value: SmartFilterValue | undefined): SmartFilterValue {
  if (!value) return { ...EMPTY_FILTER };
  return {
    version: 1,
    quickSearch: value.quickSearch || '',
    logic: value.logic === 'or' ? 'or' : 'and',
    rules: Array.isArray(value.rules) ? value.rules : [],
  };
}

function targetAssignments(
  targets: LookupTarget[],
  currentUserId: string | undefined,
  isDefault: boolean,
  includeCurrentUser: boolean,
) {
  const assignments = new Map<string, Record<string, unknown>>();
  if (includeCurrentUser && currentUserId) {
    assignments.set(`USER:${currentUserId}`, { targetType: 'USER', targetId: currentUserId, isDefault });
  }
  targets.forEach((target) => {
    if (target.type === 'user') {
      assignments.set(`USER:${target.id}`, { targetType: 'USER', targetId: target.id, isDefault });
    } else if (target.type === 'group') {
      assignments.set(`GROUP:${target.id}`, { targetType: 'GROUP', targetId: target.id, isDefault });
    } else {
      const targetCode = target.code || target.id;
      assignments.set(`SYSTEM_GROUP:${targetCode}`, { targetType: 'SYSTEM_GROUP', targetCode, isDefault });
    }
  });
  return Array.from(assignments.values());
}

export function SmartFilter({
  identity,
  fields,
  value,
  onApply,
  onClear,
  onAddClick,
  addDisabled,
  addTitle,
  addButtonClassName,
  addLearningTargetId,
  hasExternalFilters,
  hideQuickSearch = false,
  language = DEFAULT_LANGUAGE,
}: SmartFilterProps) {
  const labels = getLabels(language);
  const formId = useId();
  const { user } = useAuth();
  const { hasAnyPermission } = usePermission();
  const canShare = hasAnyPermission(['dataTableFilter:share', 'dataTableFilter:manage']);
  const tableKey = useMemo(() => dataTableStorageTableId(identity), [identity]);
  const valueRef = useRef(value);
  const onApplyRef = useRef(onApply);
  const defaultAppliedRef = useRef(false);
  const [presets, setPresets] = useState<SmartFilterPreset[]>([]);
  const [selectedPresetId, setSelectedPresetId] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [draft, setDraft] = useState<SmartFilterValue>(() => normalizeFilterValue(value));
  const [filterName, setFilterName] = useState('');
  const [makeDefault, setMakeDefault] = useState(false);
  const [targets, setTargets] = useState<LookupTarget[]>([]);
  const [targetCache, setTargetCache] = useState<LookupTarget[]>([]);
  const [selectedTargetIds, setSelectedTargetIds] = useState<string[]>(['own']);
  const [targetSearch, setTargetSearch] = useState('');
  const [loadingPresets, setLoadingPresets] = useState(false);
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const targetById = useMemo(() => {
    const map = new Map<string, LookupTarget>();
    targetCache.forEach((target) => map.set(`${target.type}:${target.id}`, target));
    return map;
  }, [targetCache]);

  const selectedTargets = useMemo(
    () => selectedTargetIds
      .filter((id) => id !== 'own')
      .map((id) => targetById.get(id))
      .filter((target): target is LookupTarget => Boolean(target)),
    [selectedTargetIds, targetById],
  );

  const targetOptions = useMemo<NonNullable<FormField['options']>>(() => {
    const visibleTargetMap = new Map<string, LookupTarget>();
    selectedTargets.forEach((target) => visibleTargetMap.set(`${target.type}:${target.id}`, target));
    targets.forEach((target) => visibleTargetMap.set(`${target.type}:${target.id}`, target));
    const visibleTargets = Array.from(visibleTargetMap.values());
    const byType = (type: LookupTarget['type']) => visibleTargets.filter((target) => target.type === type);
    return [
      {
        value: 'own',
        label: labels.onlyMe,
      },
      ...byType('systemGroup').map((target) => ({
        value: `${target.type}:${target.id}`,
        label: target.name,
        group: labels.targetSystemGroup,
        searchStr: `${target.name} ${target.description || ''} ${target.code || ''}`,
      })),
      ...byType('group').map((target) => ({
        value: `${target.type}:${target.id}`,
        label: target.name,
        group: labels.targetGroup,
        searchStr: `${target.name} ${target.email || ''}`,
      })),
      ...byType('user').map((target) => ({
        value: `${target.type}:${target.id}`,
        label: target.name,
        group: labels.targetUser,
        searchStr: `${target.name} ${target.email || ''}`,
      })),
    ];
  }, [labels.onlyMe, labels.targetGroup, labels.targetSystemGroup, labels.targetUser, selectedTargets, targets]);

  const selectedTargetSummary = useMemo(() => {
    if (selectedTargetIds.length === 0 || (selectedTargetIds.length === 1 && selectedTargetIds[0] === 'own')) {
      return labels.onlyMe;
    }
    const labelsList = selectedTargetIds.map((id) => {
      if (id === 'own') return labels.onlyMe;
      return targetById.get(id)?.name;
    }).filter(Boolean) as string[];
    if (labelsList.length <= 2) return labelsList.join(', ');
    return `${labelsList.slice(0, 2).join(', ')} +${labelsList.length - 2}`;
  }, [labels.onlyMe, selectedTargetIds, targetById]);

  const saveFormFields = useMemo<FormField[]>(() => [
    {
      name: 'filterName',
      label: labels.saveAs,
      type: 'text',
      colSpan: 4,
      required: true,
    },
    {
      name: 'makeDefault',
      label: labels.default,
      type: 'checkbox',
      colSpan: 3,
    },
    {
      name: 'selectedTargetIds',
      label: labels.target,
      type: 'multiselect',
      colSpan: 5,
      options: targetOptions,
      placeholder: labels.target,
      disabled: !canShare,
      searchPlaceholder: labels.targetSearch,
      noResultsText: loadingTargets ? labels.loading : labels.noTarget,
      showSelectAll: false,
      selectedCountLabel: () => selectedTargetSummary,
      onSearchChange: setTargetSearch,
    },
  ], [
    canShare,
    labels.default,
    labels.loading,
    labels.noTarget,
    labels.saveAs,
    labels.target,
    labels.targetSearch,
    loadingTargets,
    selectedTargetSummary,
    targetOptions,
  ]);

  const handleTargetsChange = useCallback((next: string[]) => {
    setSelectedTargetIds(next.length > 0 ? next : ['own']);
  }, []);

  useEffect(() => {
    onApplyRef.current = onApply;
  }, [onApply]);

  const loadPresets = useCallback(async () => {
    if (!tableKey) return;
    try {
      setLoadingPresets(true);
      const response = await axios.get(resolveCoreApiPath('/api/data-table-filters'), { params: { tableKey } });
      const payload = response.data?.data ?? response.data;
      const list = Array.isArray(payload?.data) ? payload.data : [];
      setPresets(list);
      const defaultPreset = list.find((preset: SmartFilterPreset) => preset.isDefault);
      if (defaultPreset && !defaultAppliedRef.current && !hasFilterValue(valueRef.current)) {
        defaultAppliedRef.current = true;
        setSelectedPresetId(defaultPreset.id);
        onApplyRef.current(normalizeFilterValue(defaultPreset.definition));
      }
    } catch {
      setPresets([]);
    } finally {
      setLoadingPresets(false);
    }
  }, [tableKey]);

  useEffect(() => {
    void loadPresets();
  }, [loadPresets]);

  useEffect(() => {
    valueRef.current = value;
    setDraft(normalizeFilterValue(value));
  }, [value]);

  useEffect(() => {
    if (!canShare || !modalOpen) return;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      setLoadingTargets(true);
      void axios
        .get(resolveCoreApiPath('/api/lookup/users-and-groups'), {
          params: { includeSystem: true, q: targetSearch || undefined },
        })
        .then((response) => {
          if (cancelled) return;
          const payload = response.data?.data ?? response.data;
          const list = Array.isArray(payload?.data) ? payload.data : [];
          setTargets(list);
          setTargetCache((current) => {
            const map = new Map<string, LookupTarget>();
            current.forEach((target) => map.set(`${target.type}:${target.id}`, target));
            list.forEach((target: LookupTarget) => map.set(`${target.type}:${target.id}`, target));
            return Array.from(map.values());
          });
        })
        .catch(() => {
          if (!cancelled) setTargets([]);
        })
        .finally(() => {
          if (!cancelled) setLoadingTargets(false);
        });
    }, 220);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [canShare, modalOpen, targetSearch]);

  const applyValue = useCallback((next: SmartFilterValue) => {
    const normalized = normalizeFilterValue(next);
    setDraft(normalized);
    setSelectedPresetId('');
    onApply(normalized);
  }, [onApply]);

  const handleQuickSearchChange = useCallback((quickSearch: string) => {
    applyValue({ ...normalizeFilterValue(value), quickSearch });
  }, [applyValue, value]);

  const handleSelectPreset = useCallback((presetId: string) => {
    setSelectedPresetId(presetId);
    if (!presetId) {
      onClear();
      return;
    }
    const preset = presets.find((item) => item.id === presetId);
    if (preset) {
      const next = normalizeFilterValue(preset.definition);
      setDraft(next);
      onApply(next);
    }
  }, [onApply, onClear, presets]);

  const handleClear = useCallback(() => {
    setSelectedPresetId('');
    setDraft({ ...EMPTY_FILTER });
    setFilterName('');
    onClear();
  }, [onClear]);

  const openModal = useCallback(() => {
    const selectedPreset = presets.find((preset) => preset.id === selectedPresetId);
    setDraft(normalizeFilterValue(value));
    setFilterName(selectedPreset?.name || '');
    setMakeDefault(Boolean(selectedPreset?.isDefault));
    setSelectedTargetIds(['own']);
    setTargetSearch('');
    setError(null);
    setMessage(null);
    setModalOpen(true);
  }, [presets, selectedPresetId, value]);

  const updateRule = useCallback((index: number, patch: Partial<SmartFilterRule>) => {
    setDraft((current) => ({
      ...current,
      rules: current.rules.map((rule, ruleIndex) => {
        if (ruleIndex !== index) return rule;
        const fieldChanged = patch.field && patch.field !== rule.field;
        const field = fields.find((item) => item.key === (patch.field || rule.field));
        return {
          ...rule,
          ...patch,
          operator: fieldChanged ? defaultOperator(field) : (patch.operator || rule.operator),
          value: fieldChanged ? '' : (patch.value ?? rule.value),
          valueTo: fieldChanged ? '' : (patch.valueTo ?? rule.valueTo),
        };
      }),
    }));
  }, [fields]);

  const addRule = useCallback(() => {
    const field = fields[0];
    if (!field) return;
    setDraft((current) => ({
      ...current,
      rules: [
        ...current.rules,
        { field: field.key, operator: defaultOperator(field), value: '' },
      ],
    }));
  }, [fields]);

  const removeRule = useCallback((index: number) => {
    setDraft((current) => ({
      ...current,
      rules: current.rules.filter((_rule, ruleIndex) => ruleIndex !== index),
    }));
  }, []);

  const handleApply = useCallback(() => {
    onApply(normalizeFilterValue(draft));
    setModalOpen(false);
  }, [draft, onApply]);

  const handleSave = useCallback(async () => {
    const name = filterName.trim();
    if (!name) {
      setError(labels.nameRequired);
      return;
    }
    try {
      setSaving(true);
      setError(null);
      setMessage(null);
      const existingPreset = selectedPresetId
        ? presets.find((preset) => preset.id === selectedPresetId)
        : presets.find((preset) => preset.name.trim().toLocaleLowerCase('tr-TR') === name.toLocaleLowerCase('tr-TR'));
      const payload = {
        appCode: getApplicationCodeForApi(),
        module: identity.module,
        page: identity.page,
        component: identity.component || 'main',
        tableKey,
        name,
        definition: normalizeFilterValue(draft),
        assignments: targetAssignments(
          selectedTargets,
          user?.id ? String(user.id) : undefined,
          makeDefault,
          selectedTargetIds.includes('own'),
        ),
      };
      const response = existingPreset
        ? await axios.patch(resolveCoreApiPath(`/api/data-table-filters/${existingPreset.id}`), payload)
        : await axios.post(resolveCoreApiPath('/api/data-table-filters'), payload);
      const savedPreset = response.data?.data ?? response.data;
      const savedPresetId = savedPreset?.id ? String(savedPreset.id) : existingPreset?.id;
      setMessage(labels.saved);
      await loadPresets();
      if (savedPresetId) setSelectedPresetId(savedPresetId);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.response?.data?.message || err?.message || labels.nameRequired);
    } finally {
      setSaving(false);
    }
  }, [draft, filterName, identity, labels, loadPresets, makeDefault, presets, selectedPresetId, selectedTargetIds, selectedTargets, tableKey, user?.id]);

  const handleSetDefault = useCallback(async () => {
    if (!selectedPresetId || !user?.id) return;
    try {
      setSaving(true);
      await axios.post(resolveCoreApiPath(`/api/data-table-filters/${selectedPresetId}/default`), {
        targetType: 'USER',
        targetId: String(user.id),
      });
      setMakeDefault(true);
      await loadPresets();
    } finally {
      setSaving(false);
    }
  }, [loadPresets, selectedPresetId, user?.id]);

  const handleDelete = useCallback(async () => {
    if (!selectedPresetId) return;
    try {
      setSaving(true);
      await axios.delete(resolveCoreApiPath(`/api/data-table-filters/${selectedPresetId}`));
      setSelectedPresetId('');
      await loadPresets();
    } finally {
      setSaving(false);
    }
  }, [loadPresets, selectedPresetId]);

  const quickSearch = value.quickSearch || '';
  const active = hasFilterValue(value);
  const clearVisible = active || Boolean(hasExternalFilters);
  const hasRuleFields = fields.length > 0;
  const safeAddLearningTargetId = guidedLearningTargetSelector(addLearningTargetId ?? '')
    ? addLearningTargetId?.trim()
    : undefined;

  return (
    <>
      <div className={`smart-filter-toolbar${hideQuickSearch ? ' is-search-hidden' : ''}`}>
        {onAddClick && (
          <button
            type="button"
            className={`smart-filter-icon-button smart-filter-add-button${addButtonClassName ? ` ${addButtonClassName}` : ''}`}
            onClick={onAddClick}
            disabled={addDisabled}
            title={addTitle || labels.add}
            aria-label={addTitle || labels.add}
            data-qp-learning-id={safeAddLearningTargetId}
          >
            <Icon name="add" size={18} />
          </button>
        )}
        {!hideQuickSearch && (
          <TableQuickSearch className="smart-filter-search" value={quickSearch}
            onChange={handleQuickSearchChange} label={labels.search} />
        )}
        <select
          className="smart-filter-preset-select"
          value={selectedPresetId}
          onChange={(event) => handleSelectPreset(event.target.value)}
          aria-label={labels.savedFilters}
          title={labels.savedFilters}
          disabled={loadingPresets}
        >
          <option value="">{loadingPresets ? labels.loading : labels.allRecords}</option>
          {presets.map((preset) => (
            <option key={preset.id} value={preset.id}>
              {preset.isDefault ? `${preset.name} *` : preset.name}
            </option>
          ))}
        </select>
        {hasRuleFields && (
          <button
            type="button"
            className={`smart-filter-icon-button smart-filter-detail-button${active ? ' is-active' : ''}`}
            onClick={openModal}
            title={labels.detail}
            aria-label={labels.detail}
            aria-expanded={modalOpen}
          >
            <Icon name="filter" size={17} />
          </button>
        )}
        {clearVisible && (
          <button
            type="button"
            className="smart-filter-icon-button smart-filter-clear-button"
            onClick={handleClear}
            title={labels.clear}
            aria-label={labels.clear}
          >
            <Icon name="clear" size={17} />
          </button>
        )}
      </div>

      <FormModal
        isOpen={hasRuleFields && modalOpen}
        onClose={() => setModalOpen(false)}
        title={labels.modalTitle}
        size="lg"
        overlayClassName="smart-filter-modal-overlay"
        contentClassName="smart-filter-form-modal"
        footer={
          <div className="smart-filter-modal-footer">
            <button type="button" className="btn-secondary smart-filter-primary-button smart-filter-apply-button" onClick={handleApply} disabled={saving}>
              {labels.apply}
            </button>
            {selectedPresetId && (
              <div className="smart-filter-selected-actions">
                <button type="button" className="btn-secondary smart-filter-secondary-button" onClick={handleSetDefault} disabled={saving}>
                  {labels.default}
                </button>
                <button type="button" className="btn-secondary smart-filter-secondary-button smart-filter-danger-button" onClick={handleDelete} disabled={saving}>
                  {labels.delete}
                </button>
              </div>
            )}
            <button type="button" className="btn-secondary smart-filter-secondary-button smart-filter-cancel-button" onClick={() => setModalOpen(false)} disabled={saving}>
              {labels.cancel}
            </button>
            <button type="submit" form={formId} className="btn-primary smart-filter-primary-button smart-filter-save-button" disabled={saving}>
              {labels.save}
            </button>
          </div>
        }
      >
        <form
          id={formId}
          className="smart-filter-modal"
          onSubmit={(event) => {
            event.preventDefault();
            void handleSave();
          }}
        >
          {(error || message) && (
            <div className={`smart-filter-state ${error ? 'is-error' : 'is-success'}`}>
              {error || message}
            </div>
          )}
          <div className="smart-filter-rule-list">
            {draft.rules.map((rule, index) => {
              const needsValueTo = rule.operator === 'between';
              return (
                <div key={`${rule.field}-${index}`} className={`smart-filter-rule-row${needsValueTo ? ' has-range' : ''}`}>
                  <div className="smart-filter-rule-form">
                    <FormBuilder
                      idPrefix={`${formId}-rule-${index}-`}
                      fields={buildRuleFormFields(fields, rule, language, labels)}
                      values={{
                        field: rule.field,
                        operator: rule.operator,
                        value: rule.value ?? '',
                        valueTo: rule.valueTo ?? '',
                      }}
                      onChange={(nextValues) => {
                        const nextField = String(nextValues.field ?? rule.field);
                        if (nextField !== rule.field) {
                          updateRule(index, { field: nextField });
                          return;
                        }
                        updateRule(index, {
                          operator: String(nextValues.operator ?? rule.operator) as SmartFilterOperator,
                          value: String(nextValues.value ?? ''),
                          valueTo: needsValueTo ? String(nextValues.valueTo ?? '') : '',
                        });
                      }}
                      language={language}
                    />
                  </div>
                  <button
                    type="button"
                    className="smart-filter-icon-button smart-filter-rule-delete-button"
                    onClick={() => removeRule(index)}
                    title={labels.removeRule}
                    aria-label={labels.removeRule}
                  >
                    <Icon name="delete" size={16} />
                  </button>
                </div>
              );
            })}
            <button type="button" className="smart-filter-add-rule" onClick={addRule}>
              <Icon name="add" size={16} />
              <span>{labels.addRule}</span>
            </button>
          </div>

          <div className="smart-filter-save-panel">
            <FormBuilder
              idPrefix={`${formId}-preset-`}
              fields={saveFormFields}
              values={{ filterName, makeDefault, selectedTargetIds }}
              onChange={(nextValues) => {
                setFilterName(String(nextValues.filterName ?? ''));
                setMakeDefault(Boolean(nextValues.makeDefault));
                handleTargetsChange(
                  Array.isArray(nextValues.selectedTargetIds)
                    ? nextValues.selectedTargetIds.map((item: unknown) => String(item))
                    : ['own'],
                );
              }}
              language={language}
              errors={error === labels.nameRequired ? { filterName: error } : {}}
            />
          </div>
        </form>
      </FormModal>
    </>
  );
}

export function createEmptySmartFilter(): SmartFilterValue {
  return { ...EMPTY_FILTER, rules: [] };
}
